import { useEffect, useRef, type ReactNode } from 'react';

/**
 * PractitionerFilterPanel — the calendar's practitioner filters, behind the Practitioners button (Antonin, 13 Oct:
 * "integrate this filter system within the practitioner button"). It drops under the Practitioners / Devices bar, over
 * the calendar, full width: the practitioner types (specialty chips) with Select all / Unselect all. The practitioners
 * themselves stay a row on the page, under the selector (Antonin, 13 Oct). Escape or a click outside closes it; a
 * dialog or popover opened from it (portalled) does not count as outside.
 */
export function PractitionerFilterPanel({ onClose, specialties, bulk }: {
  onClose: () => void;
  specialties: ReactNode;
  bulk: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const outside = (e: MouseEvent) => {
      const t = e.target as Element | null;
      if (!t || ref.current?.contains(t)) return;
      // The button that toggles the panel, and anything portalled (dialogs, popovers, menus) stay "inside".
      if (t.closest('[data-practitioners-menu], [role="dialog"], [role="menu"], [role="listbox"], [data-radix-popper-content-wrapper], [data-slot$="-content"]')) return;
      onClose();
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('keydown', esc); };
  }, [onClose]);

  return (
    <div ref={ref} role="dialog" aria-label="Practitioner filters" data-practitioner-panel
      // No scroll box of its own: the practitioners' hover cards must be free to overflow it. Anchored to the top of the
      // calendar area (right under the date row) at z-50: above the strip, the grid (≤40), the filter pill (20).
      className="absolute inset-x-0 top-0 z-50 border-b border-border-default bg-surface-card shadow-lg [&_[data-provider-strip]]:border-b-0">
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">{specialties}</div>
        <div className="flex flex-shrink-0 items-center gap-1">{bulk}</div>
      </div>
    </div>
  );
}
